import React from 'react';
import { Typeahead } from 'react-bootstrap-typeahead';
import { Button, Col, Input, Row } from 'reactstrap';
import { confidenceClass, confidenceBadge, formatCurrency } from './helpers';

const AppointmentCard = ({
    appointment, uid, position, cart,
    patientOptions, insuranceOptions, acomodationOptions, procedureOptions,
    emergencyError, isCalculating,
    doctors, clinics, degrees,
    typeaheadRefs,
    updateAppointment, removeAppointment,
    fetchPatients, fetchInsurancesByPatient, fetchSpecialty, fetchAcomodationsByClinic, fetchProceduresByInsurance,
    calculate, reloadProcedures, reloadInsurance, emergencyToggle,
    setInsurances, setAcomodations,
    openCreatePatient,
    validationError,
}) => {
    const isUnresolved = (field) => appointment.unresolved?.includes(field);
    const fieldClass = (field) => isUnresolved(field) ? 'border-danger' : 'border-success';

    return (
        <div className="ocr-appointment-card" data-appointment-uid={uid}>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                    <h6 className="mb-0">
                        Agendamento {position + 1}
                        {appointment.patient_name ? ` — ${appointment.patient_name}` : ''}
                        {appointment.time ? ` · ${appointment.time}` : ''}
                    </h6>
                    {appointment.total_confidence !== null && appointment.total_confidence !== undefined && (
                        <span className={`ocr-confidence-text ${confidenceClass(appointment.total_confidence)}`}>
                            {appointment.total_confidence}% de confiança
                        </span>
                    )}
                    {appointment.needs_review && (
                        <span className="ocr-review-badge">
                            ⚠ Revisar
                        </span>
                    )}
                </div>
                <Button color="danger" size="sm" onClick={() => removeAppointment(uid)}>
                    <i className="fa fa-trash"></i>
                </Button>
            </div>

            <Row>
                <Col sm={6}>
                    <div className="form-group mb-2">
                        <label>Paciente {confidenceBadge(appointment.patient_confidence)}</label>
                        <div className="d-flex gap-2">
                            <div className="flex-grow-1">
                                <Typeahead
                                    id={`patient-${uid}`}
                                    labelKey="name"
                                    multiple={false}
                                    options={patientOptions}
                                    selected={appointment.patient_id ? [{ id: appointment.patient_id, name: appointment.patient_name }] : []}
                                    placeholder="Buscar paciente..."
                                    emptyLabel="Nenhum paciente encontrado — clique no + para cadastrar"
                                    onInputChange={(search) => fetchPatients(search, uid)}
                                    ref={(r) => { typeaheadRefs.current[`patient-${uid}`] = r; }}
                                    onBlur={() => typeaheadRefs.current[`patient-${uid}`]?.blur()}
                                    onChange={(selected) => {
                                        if (selected.length > 0) {
                                            updateAppointment(uid, 'patient_id', selected[0].id);
                                            updateAppointment(uid, 'patient_name', selected[0].name);
                                            fetchInsurancesByPatient(selected[0].id, uid);
                                            reloadInsurance(uid, selected[0].id, appointment.insurance_name);
                                        } else {
                                            updateAppointment(uid, 'patient_id', null);
                                            updateAppointment(uid, 'patient_name', '');
                                            setInsurances(prev => ({ ...prev, [uid]: [] }));
                                        }
                                    }}
                                    inputProps={{ className: `form-control ${fieldClass('patient')}` }}
                                />
                            </div>
                            {!appointment.patient_id && (
                                <Button color="success" onClick={() => openCreatePatient(uid)} title="Cadastrar novo paciente">
                                    <i className="fa fa-plus"></i>
                                </Button>
                            )}
                        </div>
                        {!appointment.patient_id && (appointment.patient_suggestions || []).length > 0 && (
                            <div className="ocr-procedure-suggestions mt-1">
                                <small className="d-block mb-1">
                                    🔍 Lido na imagem: <strong>{appointment.patient_name}</strong>
                                    {appointment.patient_suggestions.length === 1 ? ' — achei este, confirme:' : ` — achei ${appointment.patient_suggestions.length}, selecione o correto:`}
                                </small>
                                <div className="ocr-suggestion-scroll" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {appointment.patient_suggestions.map((s) => (
                                        <Button
                                            key={s.patient_id}
                                            size="sm"
                                            color="light"
                                            className="mb-1 ocr-procedure-suggestion-btn d-block text-left w-100"
                                            onClick={() => {
                                                updateAppointment(uid, 'patient_id', s.patient_id);
                                                updateAppointment(uid, 'patient_name', s.patient_name);
                                                fetchInsurancesByPatient(s.patient_id, uid);
                                                reloadInsurance(uid, s.patient_id, appointment.insurance_name);
                                            }}
                                        >
                                            {s.patient_name}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {!appointment.patient_id && (appointment.patient_suggestions || []).length === 0 && (
                            appointment.match_info?.patient === 'ambiguous' ? (
                                <small className="text-danger d-block mt-1">
                                    Vários pacientes com esse nome — selecione o correto na busca acima.
                                </small>
                            ) : (
                                <small className="text-muted d-block mt-1">
                                    Paciente não encontrado? Cadastre clicando no <i className="fa fa-plus"></i>
                                </small>
                            )
                        )}
                        {validationError?.includes('patient_id') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Convênio {confidenceBadge(appointment.insurance_confidence)}</label>
                        <Input
                            type="select"
                            className={`form-control ${appointment.patient_id ? fieldClass('insurance') : ''}`}
                            value={appointment.insurance_id || ''}
                            disabled={!appointment.patient_id}
                            onChange={(e) => {
                                const selected = insuranceOptions.find(i => i.health_insurance.id == e.target.value);
                                updateAppointment(uid, 'insurance_id', e.target.value);
                                updateAppointment(uid, 'insurance_name', selected?.health_insurance.name || '');
                                if (e.target.value) reloadProcedures(uid, e.target.value);
                            }}
                        >
                            <option value="">Selecione o convênio</option>
                            {insuranceOptions.map((i) => (
                                <option key={i.health_insurance.id} value={i.health_insurance.id}>{i.health_insurance.name}</option>
                            ))}
                        </Input>
                        {!appointment.patient_id && (
                            <small className="text-muted d-block mt-1">Escolha o paciente primeiro para carregar os convênios.</small>
                        )}
                        {appointment.patient_id && isUnresolved('insurance') && (appointment.insurance_suggestions || []).length > 0 && (
                            <div className="ocr-procedure-suggestions mt-1">
                                <small className="d-block mb-1">
                                    🔍 Lido na imagem: <strong>{appointment.insurance_name}</strong>
                                    {appointment.insurance_suggestions.length === 1 ? ' — achei este, confirme:' : ` — achei ${appointment.insurance_suggestions.length}, selecione o correto:`}
                                </small>
                                <div className="ocr-suggestion-scroll" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {appointment.insurance_suggestions.map((s) => (
                                        <Button
                                            key={s.insurance_id}
                                            size="sm"
                                            color="light"
                                            className="mb-1 ocr-procedure-suggestion-btn d-block text-left w-100"
                                            onClick={() => {
                                                updateAppointment(uid, 'insurance_id', s.insurance_id);
                                                updateAppointment(uid, 'insurance_name', s.insurance_name);
                                                reloadProcedures(uid, s.insurance_id);
                                            }}
                                        >
                                            {s.insurance_name}
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {appointment.patient_id && isUnresolved('insurance') && (appointment.insurance_suggestions || []).length === 0 && (
                            <small className="text-danger">Não encontrado no sistema</small>
                        )}
                        {validationError?.includes('insurance_id') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Data</label>
                        <Input type="date" className={`form-control digits ${appointment.date ? 'border-success' : 'border-danger'}`} value={appointment.date || ''} onChange={(e) => updateAppointment(uid, 'date', e.target.value)} />
                        {!appointment.date && (
                            <div className="ocr-procedure-suggestions mt-1">
                                <small className="d-block">🔍 Não foi possível ler a DATA na imagem — preencha.</small>
                            </div>
                        )}
                        {validationError?.includes('date') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Hora {confidenceBadge(appointment.time_confidence)}</label>
                        <Input type="time" className={`form-control ${appointment.time ? 'border-success' : 'border-danger'}`} value={appointment.time || ''} onChange={(e) => updateAppointment(uid, 'time', e.target.value)} />
                        {!appointment.time && (
                            <div className="ocr-procedure-suggestions mt-1">
                                <small className="d-block">🔍 Não foi possível ler a HORA na imagem — preencha.</small>
                            </div>
                        )}
                        {validationError?.includes('time') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <div className="checkbox checkbox-solid-primary">
                            <Input
                                id={`emergency-${uid}`}
                                type="checkbox"
                                disabled={!appointment.date || !appointment.time || !appointment.insurance_id}
                                checked={!!appointment.emergency_id}
                                onChange={() => emergencyToggle(uid)}
                            />
                            <label htmlFor={`emergency-${uid}`}>Emergência</label>
                        </div>
                        {emergencyError && <small className="font-danger d-block">{emergencyError}</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Tipo</label>
                        <Input type="select" className="form-control border-success" value={appointment.appointment_type || 'Consulta'} onChange={(e) => updateAppointment(uid, 'appointment_type', e.target.value)}>
                            <option value="Consulta">Consulta</option>
                            <option value="Procedimento">Procedimento</option>
                        </Input>
                    </div>
                </Col>

                <Col sm={6}>
                    <div className="form-group mb-2">
                        <label>Médico {confidenceBadge(appointment.doctor_confidence)}</label>
                        <Typeahead
                            id={`doctor-${uid}`}
                            labelKey="name"
                            multiple={false}
                            options={doctors}
                            selected={appointment.doctor_id ? [{ id: appointment.doctor_id, name: appointment.doctor_name }] : []}
                            placeholder="Buscar médico..."
                            ref={(r) => { typeaheadRefs.current[`doctor-${uid}`] = r; }}
                            onBlur={() => typeaheadRefs.current[`doctor-${uid}`]?.blur()}
                            onChange={(selected) => {
                                if (selected.length > 0) {
                                    updateAppointment(uid, 'doctor_id', selected[0].id);
                                    updateAppointment(uid, 'doctor_name', selected[0].name);
                                    fetchSpecialty(selected[0].id, uid);
                                } else {
                                    updateAppointment(uid, 'doctor_id', null);
                                    updateAppointment(uid, 'doctor_name', '');
                                    updateAppointment(uid, 'specialty_id', null);
                                }
                            }}
                            inputProps={{ className: `form-control ${fieldClass('doctor')}` }}
                        />
                        {isUnresolved('doctor') && (
                            <div className="ocr-procedure-suggestions mt-1">
                                <small className="d-block mb-1">
                                    🔍 Lido na imagem: <strong>{appointment.doctor_name}</strong>
                                    {(appointment.doctor_suggestions || []).length === 0
                                        ? ' — não encontrado no sistema, busque na caixa acima.'
                                        : (appointment.doctor_suggestions.length === 1
                                            ? ' — achei este, confirme:'
                                            : ` — achei ${appointment.doctor_suggestions.length}, selecione o correto:`)}
                                </small>
                                {(appointment.doctor_suggestions || []).length > 0 && (
                                    <div className="ocr-suggestion-scroll" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {appointment.doctor_suggestions.map((s) => (
                                            <Button
                                                key={s.doctor_id}
                                                size="sm"
                                                color="light"
                                                className="mb-1 ocr-procedure-suggestion-btn d-block text-left w-100"
                                                onClick={() => {
                                                    updateAppointment(uid, 'doctor_id', s.doctor_id);
                                                    updateAppointment(uid, 'doctor_name', s.doctor_name);
                                                    updateAppointment(uid, 'specialty_id', s.specialty_id || null);
                                                    updateAppointment(uid, 'specialty_name', s.specialty_name || '');
                                                }}
                                            >
                                                {s.doctor_name}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {validationError?.includes('doctor_id') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Especialidade</label>
                        <Input
                            type="text"
                            className="form-control"
                            disabled
                            value={appointment.specialty_id ? (appointment.specialty_name || '') : ''}
                        />
                    </div>

                    <div className="form-group mb-2">
                        <label>Grau de Participação</label>
                        <Input
                            type="select"
                            className={`form-control ${appointment.degree_id ? 'border-success' : 'border-danger'}`}
                            value={appointment.degree_id || ''}
                            onChange={(e) => {
                                const selected = degrees.find(d => d.id == e.target.value);
                                updateAppointment(uid, 'degree_id', e.target.value);
                                updateAppointment(uid, 'degree_name', selected?.degree || '');
                                updateAppointment(uid, 'degree_percentage', selected?.percentage || 0);

                                if ((cart?.procedures || []).length > 0) {
                                    calculate(uid, cart.procedures, { degree_percentage: selected?.percentage || 0 });
                                }
                            }}
                        >
                            <option value="">Selecione o grau</option>
                            {degrees.map((d) => (
                                <option key={d.id} value={d.id}>{d.degree}</option>
                            ))}
                        </Input>
                        {validationError?.includes('degree_id') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Clínica</label>
                        <Typeahead
                            id={`clinic-${uid}`}
                            labelKey="name"
                            multiple={false}
                            options={clinics}
                            selected={appointment.clinic_id ? [{ id: appointment.clinic_id, name: appointment.clinic_name }] : []}
                            placeholder="Selecione a clínica..."
                            ref={(r) => { typeaheadRefs.current[`clinic-${uid}`] = r; }}
                            onBlur={() => typeaheadRefs.current[`clinic-${uid}`]?.blur()}
                            onChange={(selected) => {
                                if (selected.length > 0) {
                                    updateAppointment(uid, 'clinic_id', selected[0].id);
                                    updateAppointment(uid, 'clinic_name', selected[0].name);
                                    updateAppointment(uid, 'acomodation_id', null);
                                    fetchAcomodationsByClinic(selected[0].id, uid);
                                } else {
                                    updateAppointment(uid, 'clinic_id', null);
                                    updateAppointment(uid, 'clinic_name', '');
                                    updateAppointment(uid, 'acomodation_id', null);
                                    setAcomodations(prev => ({ ...prev, [uid]: [] }));
                                }
                            }}
                            inputProps={{ className: `form-control ${appointment.clinic_id ? 'border-success' : 'border-danger'}` }}
                        />
                        {validationError?.includes('clinic_id') && <small className="font-danger d-block">Campo obrigatório</small>}
                    </div>

                    <div className="form-group mb-2">
                        <label>Acomodação</label>
                        <Input
                            type="select"
                            className={`form-control ${appointment.acomodation_id ? 'border-success' : 'border-danger'}`}
                            value={appointment.acomodation_id || ''}
                            disabled={!appointment.clinic_id || !acomodationOptions.length}
                            onChange={(e) => {
                                updateAppointment(uid, 'acomodation_id', e.target.value);
                                if ((cart?.procedures || []).length > 0) {
                                    calculate(uid, cart.procedures, { acomodation_id: e.target.value });
                                }
                            }}
                        >
                            <option value="">Selecione a acomodação</option>
                            {acomodationOptions.map((a) => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                            ))}
                        </Input>
                    </div>

                    <div className="form-group mb-2">
                        <label>N° Guia {confidenceBadge(appointment.guide_confidence)}</label>
                        <Input className="form-control" value={appointment.guide_number || ''} onChange={(e) => updateAppointment(uid, 'guide_number', e.target.value)} placeholder="Número da guia" />
                    </div>

                    <div className="form-group mb-2">
                        <label>Observações</label>
                        <Input type="textarea" className="form-control" rows={2} value={appointment.observation || ''} onChange={(e) => updateAppointment(uid, 'observation', e.target.value)} />
                    </div>
                </Col>
            </Row>

            {/* Procedimentos */}
            <div className="mt-3">
                <label><strong>Procedimentos</strong></label>

                <div className="section-blur">
                    {(!appointment.insurance_id || !appointment.doctor_id || !appointment.degree_id) && <div className="section-blur-overlay"></div>}

                    {(appointment.procedures || []).length === 0 && (cart?.procedures || []).length === 0 && (appointment.procedure_suggestions || []).length === 0 && (
                        (appointment.read_procedures || []).length > 0 && !appointment.insurance_id ? (
                            <small className="ocr-empty-warning">
                                🔍 {(appointment.read_procedures || []).length} procedimento(s) lido(s) na imagem — escolha o convênio acima para carregá-los.
                            </small>
                        ) : (
                            <small className="ocr-empty-warning">
                                ⚠ Nenhum procedimento extraído da imagem. Adicione manualmente.
                            </small>
                        )
                    )}

                    {(appointment.procedure_suggestions || []).length > 0 && (
                        <div className="ocr-procedure-suggestions mb-2">
                            {appointment.procedure_suggestions.map((sug, sIdx) => (
                                <div key={sIdx} className="mb-2">
                                    <small className="d-block mb-1">
                                        🔍 Lido na imagem: <strong>{sug.name}</strong>
                                        {sug.candidates.length === 0
                                            ? ' — não encontrado neste convênio, busque manualmente abaixo.'
                                            : (sug.candidates.length === 1
                                                ? ' — achei este, confirme:'
                                                : ` — achei ${sug.candidates.length}, selecione o correto:`)}
                                    </small>
                                    {sug.candidates.length > 0 && (
                                        <div className="ocr-suggestion-scroll" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                            {sug.candidates.map((c) => (
                                                <Button
                                                    key={c.procedure_id}
                                                    size="sm"
                                                    color="light"
                                                    className="mb-1 ocr-procedure-suggestion-btn d-block text-left w-100"
                                                    onClick={() => {
                                                        const current = cart?.procedures || [];
                                                        const updated = [...current, { procedure_id: c.procedure_id, procedure_percentage: 0 }];
                                                        calculate(uid, updated);
                                                        const remaining = (appointment.procedure_suggestions || []).filter((_, i) => i !== sIdx);
                                                        updateAppointment(uid, 'procedure_suggestions', remaining);
                                                    }}
                                                >
                                                    {c.code} - {c.description} ({formatCurrency(c.value)})
                                                </Button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    <Typeahead
                        id={`procedure-${uid}`}
                        ref={(r) => { typeaheadRefs.current[`procedure-${uid}`] = r; }}
                        labelKey={(p) => `${p.code} - ${p.description}`}
                        multiple={false}
                        options={procedureOptions}
                        placeholder="Buscar procedimento pelo código..."
                        onInputChange={(search) => fetchProceduresByInsurance(appointment.insurance_id, search, uid)}
                        onBlur={() => typeaheadRefs.current[`procedure-${uid}`]?.blur()}
                        onChange={(selected) => {
                            if (selected.length === 0) return;
                            const current = cart?.procedures || [];
                            const updated = [...current, { procedure_id: selected[0].id, procedure_percentage: 0 }];
                            calculate(uid, updated);
                            typeaheadRefs.current[`procedure-${uid}`]?.clear();
                        }}
                        inputProps={{ className: 'form-control' }}
                    />

                    {isCalculating ? (
                        <div className="loader-box mt-2">
                            <div className="loader-4"></div>
                        </div>
                    ) : (
                        <>
                            {(cart?.procedures || []).length > 0 && (
                                <table className="table table-border-horizontal mt-2">
                                    <thead>
                                        <tr>
                                            <th>%</th>
                                            <th>Procedimento</th>
                                            <th className="ocr-procedure-actions"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.procedures.map((procedure, pIndex) => (
                                            <tr key={pIndex}>
                                                <td>
                                                    <Input type="select" className="form-control ocr-procedure-select"
                                                        value={procedure.procedure_percentage}
                                                        onChange={(e) => {
                                                            const updated = cart.procedures.map((p, i) =>
                                                                i === pIndex ? { ...p, procedure_percentage: Number(e.target.value) } : p
                                                            );
                                                            calculate(uid, updated);
                                                        }}>
                                                        <option value={100}>100%</option>
                                                        <option value={70}>70%</option>
                                                        <option value={50}>50%</option>
                                                        <option value={40}>40%</option>
                                                        <option value={30}>30%</option>
                                                        <option value={10}>10%</option>
                                                    </Input>
                                                </td>
                                                <td>
                                                    <div className="text-left">
                                                        <div className="f-w-600">{procedure.procedure_code} - {procedure.procedure_description} - {formatCurrency(procedure.original_value)}</div>
                                                        <small className="d-block">
                                                            {cart?.acomodation_value || 0}x Acomodação, {cart?.emergency_percentage || 0}% Emergência, {cart?.degree_percentage || 0}% Grau de Participação - {formatCurrency(procedure.value)}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <i className="fa fa-copy mr-2 ocr-clickable"
                                                        onClick={() => {
                                                            const current = cart?.procedures || [];
                                                            const updated = [...current, { ...procedure }];
                                                            calculate(uid, updated);
                                                        }}
                                                    />
                                                    <i className="fa fa-trash-o ocr-clickable"
                                                        onClick={() => {
                                                            const updated = cart.procedures.filter((_, i) => i !== pIndex);
                                                            calculate(uid, updated);
                                                        }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}

                            {appointment.total > 0 && (
                                <div className="text-right mt-2">
                                    <strong>Total: {formatCurrency(appointment.total)}</strong>
                                </div>
                            )}
                        </>
                    )}

                </div>
            </div>

        </div>
    );
};

export default AppointmentCard;
